import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { openModal } from '../../redux/Slices/modalSlice';
import { useCanCreateWorkspace } from '../../hooks/useCanCreateWorkspace';
import {
    fetchWorkspaces,
    fetchGuestsWorkspaces,
    optimisticAddWorkspace,
    optimisticUpdateWorkspace,
    optimisticDeleteWorkspace,
    optimisticRemoveWorkspaceMember,
    optimisticAddBoardToWorkspace,
    optimisticUpdateBoardInWorkspace,
    optimisticRemoveBoardFromWorkspace,
    optimisticAddGuestWorkspace,
    optimisticRemoveGuestWorkspace,
} from '../../redux/Slices/workspaceSlice';
import {
    optimisticUpdateBoardInLists,
    optimisticAddBoardToLists,
    optimisticRemoveBoardFromLists,
    fetchStarredBoards,
    fetchRecentBoards
} from '../../redux/Slices/boardsSlice';
// import { ActionTypes } from '../../utils/socketUtils';
import { ActionTypes } from '../../sockets/ActionTypes';
import GreetingHeader from "../Global/GreetingHeader";
import { useSocketConnection, useSocketAction } from '../../hooks/useSocketManager';

// import WorkspaceSidebar from './WorkspaceSidebar';
import BoardSection from './BoardSection';
import RecentView from './RecentView';
import TopBoards from './TopBoards';


import './scss/workspace.scss';


import { useBoard } from '../../context/BoardContext';
import History2 from '../icons/History2';
import AwardStar from '../icons/AwardStar';

import Groups2 from '../icons/Groups2';
import DeveloperBoard from '../icons/DeveloperBoard';
import Workspaces from '../icons/Workspaces';
import Diamond from '../icons/Diamond';
import WorkspacePremium from '../icons/WorkspacePremium';
import BouncingDots from '../Global/BouncingDots';
import { Payments, Settings } from '../icons';
import { isWorkspaceAdmin } from '../../utils/helpers';
import { isMainZoobbeDomain, isLocalDevelopment } from '../../utils/domainUtils';


const WorkSpace = () => {

    const { workspaces, guestWorkspaces, workspaceMembers, status, guestStatus, error } = useSelector(state => state.workspaces);
    const { user } = useSelector((state) => state.user);
    const canCreateWorkspace = useCanCreateWorkspace();

    const userId = user?.user?._id;
    let isAdmin = false;


    const dispatch = useDispatch();

    const { setBoard } = useBoard();


    const [openWorkspaces, setOpenWorkspaces] = useState({});
    const lastFetchTimeRef = useRef(Date.now()); // Track last fetch time

    const { recentBoards } = useSelector((state) => state.boards);


    const { starredBoards } = useSelector((state) => state.boards);



    useEffect(() => {
        dispatch(fetchRecentBoards({ limit: 10 }));
        dispatch(fetchStarredBoards());
        setBoard({ actionLists: [] });
    }, [dispatch]);



    const handleCreateWorkspace = () => {
        dispatch(openModal({ modalType: 'CREATE_WORKSPACE', modalData: {} }));
    };

    const toggleOptions = (workspaceName) => {
        setOpenWorkspaces(prevState => ({
            ...prevState,
            [workspaceName]: !prevState[workspaceName]
        }));
    };

    useEffect(() => {
        setOpenWorkspaces(prev => {
            const updated = { ...prev };
            workspaces.forEach(workspace => {
                if (!(workspace.name in updated)) {
                    updated[workspace.name] = true;
                }
            });
            guestWorkspaces.forEach(workspace => {
                if (!(workspace.name in updated)) {
                    updated[workspace.name] = true;
                }
            });
            return updated;
        });
    }, [workspaces, guestWorkspaces]);

    // Load workspaces on component mount
    useEffect(() => {
        if (status === 'idle') {
            dispatch(fetchWorkspaces());
        }
        if (guestStatus === 'idle') {
            dispatch(fetchGuestsWorkspaces());
        }
    }, [dispatch, status, guestStatus]);

    // Initialize socket connection (app-wide)
    useSocketConnection();

    // Real-time event handlers for workspace changes
    useSocketAction([
        ActionTypes.WORKSPACE_CREATED,
        ActionTypes.WORKSPACE_UPDATED,
        ActionTypes.WORKSPACE_DELETED,
        ActionTypes.MEMBER_ADDED,
        ActionTypes.MEMBER_REMOVED,
        ActionTypes.ROLE_CHANGED,
        ActionTypes.ROLE_UPDATED,
        ActionTypes.BOARD_CREATED,
        ActionTypes.BOARD_UPDATED,
        ActionTypes.BOARD_DELETED,
    ], (data) => {
        // Skip events triggered by current user (already handled optimistically)
        if (data.reqUser === user?.user?._id) return;

        console.log('[Workspace] Real-time event:', data.action);

        switch (data.action) {
            case ActionTypes.WORKSPACE_CREATED:
                // ✅ Add workspace using optimistic action (no API call)
                if (data.workspace) {
                    dispatch(optimisticAddWorkspace(data.workspace));
                }
                break;

            case ActionTypes.WORKSPACE_UPDATED:
                // ✅ Update workspace using optimistic action (no API call)
                if (data.workspaceId && data.updatedFields) {
                    dispatch(optimisticUpdateWorkspace({
                        workspaceId: data.workspaceId,
                        updates: data.updatedFields
                    }));
                }
                break;

            case ActionTypes.WORKSPACE_DELETED:
                // ✅ Delete workspace using optimistic action (no API call)
                if (data.workspaceId) {
                    dispatch(optimisticDeleteWorkspace(data.workspaceId));
                }
                break;

            case ActionTypes.MEMBER_ADDED:
                // Handle both workspace member and board member events
                if (data.workspaceId && data.workspace) {
                    // ✅ Workspace member added - if current user was added, add workspace
                    const isCurrentUserAdded = data.newMembers?.includes(user?.user?._id);

                    if (isCurrentUserAdded) {
                        // Don't add WL-bound workspaces when on zoobbe.com (they can't be accessed from here)
                        const isZoobbe = isMainZoobbeDomain() || isLocalDevelopment();
                        const isWLBound = data.workspace?.whiteLabelBinding?.instanceId;
                        if (!(isZoobbe && isWLBound)) {
                            dispatch(optimisticAddWorkspace(data.workspace));
                        }
                    }

                    // Update member list for all workspace members
                    if (data.members) {
                        // We could update workspace members here if needed
                        // For now, the workspace will be refetched or updated via other means
                    }
                } else if (data.boardId && data.board) {
                    // ✅ Board member added - if the current user was added, show the board
                    // The backend sends full board data for newly added members
                    const isCurrentUserAdded = data.newMembers?.some(
                        nm => nm.user.toString() === user?.user?._id
                    );

                    if (isCurrentUserAdded) {
                        // Check if this is a guest workspace (user added to board but not workspace member)
                        if (data.guestWorkspace) {
                            // Don't add WL-bound workspaces when on zoobbe.com
                            const isZoobbe = isMainZoobbeDomain() || isLocalDevelopment();
                            const isWLBound = data.guestWorkspace.whiteLabelBinding?.instanceId;
                            if (!(isZoobbe && isWLBound)) {
                                dispatch(optimisticAddGuestWorkspace(data.guestWorkspace));
                                dispatch(optimisticAddBoardToLists(data.board));
                            }
                        } else if (data.board.workspace) {
                            // Regular workspace member - add board to workspace
                            dispatch(optimisticAddBoardToWorkspace({
                                workspaceId: data.board.workspace,
                                board: data.board
                            }));

                            // Also add to recent/starred
                            dispatch(optimisticAddBoardToLists(data.board));
                        }
                    } else {
                        // Update board members in existing board (if already visible)
                        dispatch(optimisticUpdateBoardInWorkspace({
                            boardId: data.boardId,
                            updates: { members: data.members }
                        }));

                        // Also update in recent/starred lists
                        dispatch(optimisticUpdateBoardInLists({
                            boardId: data.boardId,
                            updates: { members: data.members }
                        }));
                    }
                }
                break;

            case ActionTypes.MEMBER_REMOVED:
                // Handle both workspace member and board member events
                if (data.workspaceId && !data.boardId) {
                    // ✅ Workspace member removed
                    if (data.memberId === user?.user?._id) {
                        // Current user was removed from workspace - remove workspace from list
                        dispatch(optimisticDeleteWorkspace(data.workspaceId));
                    } else {
                        // Another member was removed - update member list
                        dispatch(optimisticRemoveWorkspaceMember({
                            workspaceId: data.workspaceId,
                            memberId: data.memberId
                        }));
                    }
                } else if (data.boardId && data.memberId) {
                    // ✅ Board member removed - if the current user was removed, hide the board
                    if (data.memberId === user?.user?._id) {
                        // Remove the board from workspace boards list
                        dispatch(optimisticRemoveBoardFromWorkspace(data.boardId));

                        // Also remove from recent/starred lists
                        dispatch(optimisticRemoveBoardFromLists(data.boardId));
                    } else {
                        // Update board members in existing board (if already visible)
                        dispatch(optimisticUpdateBoardInWorkspace({
                            boardId: data.boardId,
                            updates: { members: data.members }
                        }));

                        // Also update in recent/starred lists
                        dispatch(optimisticUpdateBoardInLists({
                            boardId: data.boardId,
                            updates: { members: data.members }
                        }));
                    }
                }
                break;

            case ActionTypes.ROLE_CHANGED:
            case ActionTypes.ROLE_UPDATED:
                // ✅ Role updated - refetch workspace to get updated permissions
                // This affects permissions, so we need fresh data
                if (data.workspaceId) {
                    const now = Date.now();
                    if (now - lastFetchTimeRef.current > 2000 && status !== 'loading') {
                        dispatch(fetchWorkspaces());
                        lastFetchTimeRef.current = now;
                    }
                }
                break;

            case ActionTypes.BOARD_CREATED:
                // ✅ Add board to workspace boards list and recent/starred lists
                if (data.board) {
                    // Add to workspace boards list
                    if (data.board.workspace || data.workspaceId) {
                        dispatch(optimisticAddBoardToWorkspace({
                            workspaceId: data.board.workspace || data.workspaceId,
                            board: data.board
                        }));
                    }

                    // Also add to recent/starred lists
                    dispatch(optimisticAddBoardToLists(data.board));

                    // Also refresh starred/recent if this might affect them
                    if (data.board.starred || data.isRecent) {
                        dispatch(fetchStarredBoards({ useCache: false }));
                        dispatch(fetchRecentBoards({ limit: 10, useCache: false }));
                    }
                }
                break;

            case ActionTypes.BOARD_UPDATED:
                // ✅ Update board in workspace boards list and recent/starred lists
                if (data.boardId && (data.board || data.updatedFields)) {
                    const updates = data.board || data.updatedFields;

                    // Update in workspace boards list
                    dispatch(optimisticUpdateBoardInWorkspace({
                        boardId: data.boardId,
                        updates
                    }));

                    // Also update in recent/starred lists
                    dispatch(optimisticUpdateBoardInLists({
                        boardId: data.boardId,
                        updates
                    }));
                }
                break;

            case ActionTypes.BOARD_DELETED:
                // ✅ Remove board from workspace boards list and recent/starred lists
                if (data.boardId) {
                    // Remove from workspace boards list
                    dispatch(optimisticRemoveBoardFromWorkspace(data.boardId));

                    // Also remove from recent/starred lists
                    dispatch(optimisticRemoveBoardFromLists(data.boardId));
                }
                break;

            default:
                console.log('[Workspace] Unhandled real-time action:', data.action);
        }
    }, [dispatch, user?.user?._id, status]);


    return (
        <>
            <div className="zppbbe-workspace-wrapper">
                <div className="zoobbe-workspace-container">

                    <div className='zoobbe-main-content-container'>
                        <div className="zoobbe-main-content">
                            <GreetingHeader />

                            {
                                starredBoards?.length > 0 && (
                                    <div className="zoobbe-recently-viewed">
                                        <section className="zoobbe-your-workspaces">
                                            <h2>
                                                <AwardStar />
                                                Starred Boards</h2>
                                            <div className="zoobbe-workspace-section">
                                                <TopBoards type={'STARRED'} boards={starredBoards} />
                                            </div>

                                        </section>
                                    </div>
                                )
                            }
                            {
                                recentBoards?.length > 0 && (
                                    <div className="zoobbe-recently-viewed">
                                        <section className="zoobbe-your-workspaces">
                                            <h2>
                                                <History2 />
                                                Recently viewed</h2>
                                            <div className="zoobbe-workspace-section">
                                                <RecentView />
                                            </div>

                                        </section>
                                    </div>
                                )
                            }




                            {
                                ((status === 'loading' || guestStatus === 'loading') && workspaces?.length < 1) ? (
                                    // Show a loading spinner while loading
                                    <div className="spinner-container">
                                        <BouncingDots />
                                    </div>
                                ) : workspaces?.length > 0 ? (
                                    // Show workspaces if available
                                    <section className="zoobbe-your-workspaces">
                                        <h2>Your Workspaces</h2>
                                        {workspaces.map((workspace, index) => {

                                            isAdmin = isWorkspaceAdmin(workspace, userId)

                                            return (
                                                <div className="zoobbe-workspace-section" key={workspace._id}>
                                                    <div className="boards-page-board-section-header">
                                                        <div className={`zoobbe-workspace-card ${workspace?.subscription?.planType || 'Free'}`}>

                                                            {
                                                                workspace?.subscription ? (
                                                                    <>
                                                                        {
                                                                            workspace?.subscription?.planType === 'Premium' ? (
                                                                                <WorkspacePremium
                                                                                    className={'zoobbe-workspace-icon'}
                                                                                    tooltipContent={workspace?.subscription?.planType}
                                                                                    tooltipPosition="top"
                                                                                />
                                                                            ) : <Diamond
                                                                                className={'zoobbe-workspace-icon'}
                                                                                tooltipContent={workspace?.subscription?.planType}
                                                                                tooltipPosition="top"
                                                                            />
                                                                        }

                                                                    </>
                                                                ) : (
                                                                    <Workspaces
                                                                        className={'zoobbe-workspace-icon'}
                                                                        tooltipContent="Free"
                                                                        tooltipPosition="top"
                                                                    />
                                                                )
                                                            }

                                                            <span className="zoobbe-workspace-title">{workspace.name}</span>
                                                        </div>
                                                        <div className="zoobbe-workspace-actions">
                                                            <Link to={`/w/${workspace.shortId}/boards`} data-tooltip-content="Boards" data-tooltip-position="top">
                                                                <DeveloperBoard size={18} />
                                                            </Link>
                                                            <Link to={`/w/${workspace.shortId}/members`} data-tooltip-content="Members" data-tooltip-position="top">
                                                                <Groups2 />

                                                            </Link>
                                                            <Link to={`/w/${workspace.shortId}/settings`} data-tooltip-content="Settings" data-tooltip-position="top">
                                                                <Settings />
                                                            </Link>

                                                            {isAdmin && workspace?.subscription && workspace?.subscription?.planType !== 'Free' && (
                                                                <Link to={`/w/${workspace.shortId}/billing`}>
                                                                    Billing
                                                                </Link>
                                                            )}

                                                            {
                                                                isAdmin && (
                                                                    workspace?.subscription && workspace?.subscription?.planType !== 'Free' ? null : (
                                                                        <Link to={`/w/${workspace.shortId}/billing`}>
                                                                            Upgrade
                                                                        </Link>
                                                                    )
                                                                )
                                                            }




                                                        </div>
                                                    </div>
                                                    <BoardSection workspace={workspace} defaultSelectedOption={index} />
                                                </div>
                                            )
                                        })}
                                    </section>
                                ) : ((status === 'succeeded' && guestStatus === 'succeeded') && workspaces?.length < 1) && (
                                    <div style={{ textAlign: 'center', padding: '40px', maxWidth: '520px' }}>
                                        <span
                                            className="material-icons-outlined"
                                            style={{
                                                fontSize: '120px',
                                                color: '#9E9E9E',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            assignment_add
                                        </span>


                                        <p style={{ fontSize: '16px', color: '#757575', marginBottom: '20px' }}>
                                            You don't have any workspace. Workspaces help you organize tasks and collaborate with your team. Create your first workspace to get started!
                                        </p>

                                        {canCreateWorkspace && <button
                                            onClick={handleCreateWorkspace}
                                            style={{
                                                padding: '10px 20px',
                                                fontSize: '16px',
                                                color: '#fff',
                                                backgroundColor: '#007BFF',
                                                border: 'none',
                                                borderRadius: '5px',
                                                cursor: 'pointer',
                                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                                transition: 'background-color 0.3s, box-shadow 0.3s',
                                            }}
                                            onMouseEnter={(e) => {
                                                e.target.style.backgroundColor = '#0056b3';
                                                e.target.style.boxShadow = '0 6px 8px rgba(0, 0, 0, 0.2)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.backgroundColor = '#007BFF';
                                                e.target.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                                            }}
                                        >
                                            Create Workspace
                                        </button>}
                                    </div>

                                )
                            }


                            {
                                guestWorkspaces.length > 0 && (
                                    <section className="zoobbe-guest-workspaces">
                                        <h2>Guest Workspaces</h2>
                                        {guestWorkspaces.map((workspace, defaultSelectedOption) => (
                                            <div className="zoobbe-workspace-section" key={workspace._id}>
                                                <div className='boards-page-board-section-header'>
                                                    <div className="zoobbe-workspace-card">
                                                        <span className="zoobbe-workspace-icon">{workspace.name ? workspace.name.charAt(0) : ''}</span>
                                                        <span className="zoobbe-workspace-title">{workspace.name}</span>
                                                    </div>
                                                </div>

                                                <BoardSection workspace={workspace} defaultSelectedOption={defaultSelectedOption} isGuests={true} />

                                            </div>

                                        ))}

                                        {
                                            status === 'loading' && (workspaces?.length === 0 || guestWorkspaces?.length === 0) && (
                                                <div className='spinner-container'>
                                                    <BouncingDots />
                                                </div>
                                            )
                                        }

                                    </section>
                                )
                            }
                        </div>
                    </div>
                </div >
            </div >

        </>

    );
};

export default WorkSpace;
